import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  GraduationCap, Clock, ArrowLeft, BookOpen, Sparkles, ArrowUpRight, Search,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { PageHeader } from "@/components/common/PageHeader";
import { GlassCard } from "@/components/common/GlassCard";
import { StatusPill } from "@/components/common/StatusPill";
import { LoadingState, EmptyState } from "@/components/common/States";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Phase 12 — Family Education Library (Layer 4 · Family)
// ---------------------------------------------------------------------------
// A curated, read-only knowledge base. Two views: index grid + article reader.
// Content is server-side static (see backend/education_content.py).
// ---------------------------------------------------------------------------

const TONE_TILE = {
  cyan:  { ring: "ring-cyan-300/30",   chip: "bg-cyan-300/15 text-cyan-200",   icon: "text-cyan-200"  },
  rose:  { ring: "ring-rose-400/30",   chip: "bg-rose-400/15 text-rose-200",   icon: "text-rose-200"  },
  cream: { ring: "ring-amber-200/30",  chip: "bg-amber-200/15 text-amber-100", icon: "text-amber-100" },
  amber: { ring: "ring-amber-300/30",  chip: "bg-amber-300/15 text-amber-200", icon: "text-amber-200" },
};

export default function EducationPage() {
  useDocumentTitle("OmniDraftLab · Education");
  const [data, setData] = useState({ categories: [], articles: [] });
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // article object or null
  const [activeBody, setActiveBody] = useState(null); // body_md
  const [activeBodyLoading, setActiveBodyLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/education/articles")
      .then((r) => { if (!cancelled) setData(r.data); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const open = async (article) => {
    setActive(article);
    setActiveBodyLoading(true);
    try {
      const r = await api.get(`/education/articles/${article.id}`);
      setActiveBody(r.data.body_md);
    } finally {
      setActiveBodyLoading(false);
    }
  };

  const close = () => { setActive(null); setActiveBody(null); };

  const filtered = useMemo(() => {
    let xs = data.articles || [];
    if (filter !== "all") xs = xs.filter((a) => a.category === filter);
    const q = query.trim().toLowerCase();
    if (q) xs = xs.filter((a) => a.title.toLowerCase().includes(q) || a.summary.toLowerCase().includes(q));
    return xs;
  }, [data.articles, filter, query]);

  const catByKey = useMemo(
    () => Object.fromEntries((data.categories || []).map((c) => [c.key, c])),
    [data.categories]
  );

  if (loading) return <LoadingState />;

  if (active) {
    return (
      <ArticleReader
        article={active}
        body={activeBody}
        loading={activeBodyLoading}
        category={catByKey[active.category]}
        onClose={close}
      />
    );
  }

  return (
    <div className="max-w-[1500px] mx-auto" data-testid="education-page">
      <PageHeader
        title="Family Education"
        subtitle="The draft, demystified. Hand-written by the OmniDraftLab advisory team — read at your own pace."
      />

      {/* Filter row */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-2">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-3" />
          <Input
            data-testid="education-search"
            placeholder="Search articles…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="bg-white/5 border-white/10 pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <FilterPill active={filter === "all"} onClick={() => setFilter("all")} testId="education-filter-all">
            All
          </FilterPill>
          {(data.categories || []).map((c) => (
            <FilterPill
              key={c.key}
              active={filter === c.key}
              onClick={() => setFilter(c.key)}
              testId={`education-filter-${c.key}`}
            >
              {c.label}
            </FilterPill>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="mt-8">
          <EmptyState icon={BookOpen} title="No articles match" description="Try clearing the search or filter." />
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-12 gap-4 md:gap-5">
          {filtered.map((a, i) => (
            <ArticleTile
              key={a.id}
              article={a}
              category={catByKey[a.category]}
              index={i}
              onOpen={() => open(a)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FilterPill({ active, onClick, testId, children }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={cn(
        "text-xs px-3 py-1 rounded-full ring-1 transition",
        active
          ? "bg-cyan-300 text-[#04111A] ring-cyan-300 font-semibold"
          : "bg-white/[0.04] text-2 ring-white/10 hover:ring-cyan-300/30 hover:text-1"
      )}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Article tile (index card)
// ---------------------------------------------------------------------------
function ArticleTile({ article, category, index, onOpen }) {
  const tone = TONE_TILE[category?.tone] || TONE_TILE.cyan;
  return (
    <motion.button
      data-testid={`education-article-${article.id}`}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.04, ease: [0.16, 1, 0.3, 1] }}
      onClick={onOpen}
      className={cn(
        "col-span-12 md:col-span-6 lg:col-span-4 text-left group glass glass-hover p-6 ring-1 ring-white/10 hover:ring-cyan-300/30 transition flex flex-col h-full"
      )}
    >
      <div className="flex items-center justify-between">
        <span className={cn(
          "inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2.5 py-0.5 rounded-full",
          tone.chip
        )}>
          <BookOpen className={cn("h-3 w-3", tone.icon)} /> {category?.label || article.category}
        </span>
        <span className="text-3 text-[11px] inline-flex items-center gap-1">
          <Clock className="h-3 w-3" /> {article.read_minutes} min
        </span>
      </div>
      <h3 className="mt-4 text-1 text-lg sm:text-xl font-semibold tracking-tight leading-snug">
        {article.title}
      </h3>
      <p className="mt-2 text-2 text-sm leading-relaxed flex-1">
        {article.summary}
      </p>
      <div className="mt-4 inline-flex items-center gap-1 text-cyan-300 text-xs font-medium group-hover:gap-2 transition-all">
        Read article <ArrowUpRight className="h-3.5 w-3.5" />
      </div>
    </motion.button>
  );
}

// ---------------------------------------------------------------------------
// Article reader
// ---------------------------------------------------------------------------
function ArticleReader({ article, body, loading, category, onClose }) {
  const tone = TONE_TILE[category?.tone] || TONE_TILE.cyan;
  return (
    <div className="max-w-[920px] mx-auto" data-testid="education-reader">
      <Button
        data-testid="education-back"
        variant="ghost"
        onClick={onClose}
        className="text-2 hover:text-white -ml-3"
      >
        <ArrowLeft className="h-4 w-4 mr-1" /> All articles
      </Button>

      <AnimatePresence mode="wait">
        <motion.div
          key={article.id}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        >
          <GlassCard className="mt-4 p-7 sm:p-10">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <span className={cn(
                "inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2.5 py-0.5 rounded-full",
                tone.chip
              )}>
                <Sparkles className={cn("h-3 w-3", tone.icon)} /> {category?.label || article.category}
              </span>
              <StatusPill tone="neutral"><Clock className="h-3 w-3" /> {article.read_minutes} min read</StatusPill>
            </div>
            <h1 className="mt-5 text-1 text-3xl sm:text-4xl font-semibold tracking-tight leading-tight">
              {article.title}
            </h1>
            <p className="mt-3 text-2 text-base sm:text-lg leading-relaxed">
              {article.summary}
            </p>
            <div className="mt-8 h-px bg-white/10" />
            {loading || !body ? (
              <LoadingState />
            ) : (
              <div className="markdown-body mt-6 text-2 prose prose-invert prose-headings:text-1 prose-strong:text-1 prose-li:my-1 max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
              </div>
            )}
            <div className="mt-10 flex items-center gap-2 text-3 text-[11px] uppercase tracking-[0.2em] italic">
              <GraduationCap className="h-3.5 w-3.5" /> OmniDraftLab Family Education
            </div>
          </GlassCard>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
